//src/components/admin/admin.functions.js
import AdminService from '../../services/admin.services';
import { handleUserExport } from '../../utils/exportUtils';

/**
 * Get all user profiles with profile pictures
 */
export const fetchAllUserProfiles = async (page = 1, pageSize = 25, search = '', sortField = 'email', sortDirection = 'asc') => {
  try {
    const res = await AdminService.getAllProfiles(page, pageSize, search, sortField, sortDirection);
    // Check if user profile data has users array
    if (res.data && res.data.users && Array.isArray(res.data.users)) {
      // Process profile picture data for frontend use
      const processedData = res.data.users.map(user => {
        // If the user has a profilePictureKey or profilePictureUrl, add profilePicture property for frontend use
        if (user.profilePictureKey || user.profilePictureUrl) {
          user.profilePicture = user.profilePictureUrl || true;
        }
        return user;
      });
      return {
        success: true,
        data: processedData,
        pagination: res.data.pagination
      };
    }
    return {
      success: true,
      data: res.data.users || [],
      pagination: res.data.pagination
    };
  } catch (err) {
    return { success: false, error: `Whoops! Something went wrong. ${err}` };
  }
};

/**
 * Fetch a single user profile with profile picture
 */
export const fetchUserProfile = async (email) => {
  try {
    const res = await AdminService.getProfile(email);
    return { success: true, data: res.data };
  } catch (err) {
    return { success: false, error: `Whoops! Something went wrong. ${err}` };
  }
};

/**
 * Handle updating a user's profile (groups only)
 */
export const updateUserProfile = async (profileData, groupsPayload) => {
  // Only update groups and keep other fields as they are
  const body = {
    'email': profileData.email,
    'username': profileData.username,
    'permissions': profileData.permissions || [],
    'groups': groupsPayload,
    'sendgridpermissions': profileData.sendgridpermissions || [],
    'mediarelationspermissions': profileData.mediarelationspermissions || [],
    'companiespermissions': profileData.companiespermissions || [],
    'dashboardpermissions': profileData.dashboardpermissions || [],
    'twitterxpermissions': profileData.twitterxpermissions || [],
    'entitlementpermissions': profileData.entitlementpermissions || [],
    'intercompermissions': profileData.intercompermissions || [],
    'toolspermissions': profileData.toolspermissions || [],
    'auth0permissions': profileData.auth0permissions || [],
    'scriptRunnerv2permissions': profileData.scriptRunnerv2permissions || [],
    'blocked': profileData.blocked
  };

  try {
    const res = await AdminService.updateProfile(body);
    if (res.status === 200) {
      const updatedItems = res.data.updatedItems || [];
      const statusChanged = updatedItems.includes('blocked status');
      return {
        success: true,
        message: statusChanged ?
          `User ${profileData.blocked ? 'blocked' : 'unblocked'} and profile updated successfully!` :
          'User profile updated successfully!'
      };
    }
    return { success: false, error: 'Failed to update profile' };
  } catch (err) {
    return { success: false, error: `Whoops! Something went wrong. ${err}` };
  }
};

/**
 * Add a new user with all permissions
 */
export const addNewUser = async (userInput, permissions) => {
  // Validate email format
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  if (!emailRegex.test(userInput)) {
    return { success: false, error: 'Please enter a valid email address' };
  }

  if (!userInput) {
    return { success: false, error: 'User email cannot be blank.' };
  }

  try {
    const res = await AdminService.addProfile(permissions);
    if (res.status === 200) {
      const userRestored = res.data.updatedItems?.includes('user restored');
      return {
        success: true,
        message: userRestored ? 'User restored successfully!' : 'Added user successfully!'
      };
    }
    return { success: false, error: 'Failed to add user' };
  } catch (err) {
    return { success: false, error: `${err}` };
  }
};

/**
 * Delete a user profile
 */
export const deleteUserProfile = async (email) => {
  try {
    const result = await AdminService.deleteProfile(email);
    if (result.status === 200) {
      return { success: true, message: 'User deleted successfully!' };
    }
    return { success: false, error: 'Failed to delete user' };
  } catch (err) {
    return { success: false, error: `Failed to delete user: ${err.message}` };
  }
};

/**
 * Hard delete a user profile (completely removes from database)
 */
export const hardDeleteUserProfile = async (email) => {
  try {
    const result = await AdminService.hardDeleteProfile(email);
    if (result.status === 200) {
      return { success: true, message: 'User permanently deleted successfully!' };
    }
    return { success: false, error: 'Failed to permanently delete user' };
  } catch (err) {
    return { success: false, error: `Failed to permanently delete user: ${err.message}` };
  }
};

/**
 * Block or unblock a user
 */
export const toggleBlockUser = async (email, blockAction) => {
  try {
    const result = await AdminService.blockUser(email, blockAction);
    if (result.status === 200) {
      return {
        success: true,
        message: blockAction ? 'User blocked successfully!' : 'User unblocked successfully!'
      };
    }
    return { success: false, error: `Failed to ${blockAction ? 'block' : 'unblock'} user` };
  } catch (err) {
    return {
      success: false,
      error: `Failed to ${blockAction ? 'block' : 'unblock'} user: ${err.message}`
    };
  }
};

/**
 * Export users data with support for specific fixed columns
 * @param {Object} options - Optional configuration for the export
 * @param {String} options.filename - Custom filename for the export
 */
export const exportUsers = async (options = {}) => {
  try {
    const response = await AdminService.exportUsers();
    if (response.status === 200) {
      // Check if we have user profile data
      if (!response.data || !Array.isArray(response.data)) {
        return { success: false, error: 'No user data received from server' };
      }

      // Generate filename with timestamp for uniqueness
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = options.filename || `gsd-users-export-${timestamp}.csv`;

      // Define only the specifically requested fields
      const fixedFields = {
        identityFields: ['_id', 'firstName', 'lastName', 'preferredName', 'email'],
        groupsField: ['groups'],
        permissionFields: [
          'permissions',
          'sendgridpermissions',
          'dashboardpermissions',
          'twitterxpermissions',
          'entitlementpermissions',
          'intercompermissions',
          'toolspermissions',
          'mediarelationspermissions',
          'companiespermissions'
        ],
        activityFields: ['lastLogin']
      };

      // Set up custom export options to restrict to only the specified fields
      const exportOptions = {
        fieldGroups: fixedFields,
        restrictToFieldGroups: true, // Only include fields from our defined groups
        ...options
      };

      // Use handleUserExport with the restricted fields
      const successful = handleUserExport(response.data, [], filename, exportOptions);
      if (successful) {
        return {
          success: true,
          message: `${response.data.length} users exported successfully!`,
          count: response.data.length
        };
      }
    }
    return { success: false, error: 'Failed to export users' };
  } catch (err) {
    return { success: false, error: `Failed to export users: ${err.message}` };
  }
};

/**
 * Parse name from email (e.g., firstname.lastname@meltwater.com)
 */
export const parseNameFromEmail = (email) => {
  if (!email) return { firstName: '', lastName: '' };

  try {
    // Extract the first part of email (before @)
    const namePart = email.split('@')[0];

    // Check if the name follows the Firstname.Lastname format
    if (namePart.includes('.')) {
      const [firstName, lastName] = namePart.split('.');
      return {
        firstName: firstName.charAt(0).toUpperCase() + firstName.slice(1),
        lastName: lastName.charAt(0).toUpperCase() + lastName.slice(1)
      };
    } else {
      // If there's no dot, just return the full name as firstName
      return {
        firstName: namePart.charAt(0).toUpperCase() + namePart.slice(1),
        lastName: ''
      };
    }
  } catch (error) {
    console.error("Error parsing name from email:", error);
    return { firstName: '', lastName: '' };
  }
};

/**
 * Format the last active date in a user-friendly way
 */
export const formatlastLogin = (dateString) => {
  if (!dateString) return 'Never';

  const lastLogin = new Date(dateString);
  const now = new Date();
  const diffMs = now - lastLogin;
  const diffSec = Math.round(diffMs / 1000);
  const diffMin = Math.round(diffSec / 60);
  const diffHour = Math.round(diffMin / 60);
  const diffDays = Math.round(diffHour / 24);
  const diffMonths = Math.round(diffDays / 30);

  // Format based on how long ago it was
  if (diffSec < 60) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  if (diffDays < 30) return `${diffDays}d ago`;
  if (diffMonths < 12) return `${diffMonths}mo ago`;

  // If more than a year, show the date
  return lastLogin.toLocaleDateString();
};

/**
 * Fetch last active time for users from the usagetracking collection
 * @param {Array} users - Array of user objects with email property
 * @returns {Object} - Object with success status and last active data
 */
export const fetchLastActiveTime = async (users) => {
  try {
    const emails = users.map(user => user.email);
    const response = await AdminService.getLastActiveTimes(emails);

    if (response.status === 200) {
      return {
        success: true,
        data: response.data.lastActiveData
      };
    }

    throw new Error(response.data?.error || 'Failed to fetch last active data');
  } catch (err) {
    console.error('Error fetching last active data:', err);
    return {
      success: false,
      error: err.message
    };
  }
};

/**
 * Create permission handlers for different permission types
 * @param {Array} stateArray - Current state array
 * @param {Function} setStateArray - Function to update state array
 * @param {Function} setPayload - Function to update payload
 * @returns {Function} - Event handler function
 */
export const createPermissionHandler = (stateArray, setStateArray, setPayload) => {
  return (event) => {
    const val = event.target.value;
    const newArray = [...stateArray];

    if (event.target.checked) {
      if (newArray.indexOf(val) === -1) {
        newArray.push(val);
      }
    } else {
      const index = newArray.indexOf(val);
      if (index !== -1) {
        newArray.splice(index, 1);
      }
    }

    setStateArray(newArray);
    setPayload(newArray);
  };
};

/**
 * Check if a user with the given email exists but is marked as deleted
 * @param {String} email - The email to check
 * @returns {Object} - Object with exists flag and user data if found
 */
export const checkUserExists = async (email) => {
  try {
    // Call a special API endpoint to check if a user exists but is deleted
    const response = await AdminService.checkUserExists(email);

    if (response.status === 200) {
      return {
        success: true,
        exists: response.data.exists,
        isDeleted: response.data.isDeleted,
        userData: response.data.userData
      };
    }

    return {
      success: false,
      exists: false,
      error: 'Failed to check if user exists'
    };
  } catch (err) {
    console.error('Error checking if user exists:', err);
    return {
      success: false,
      exists: false,
      error: err.message
    };
  }
};

/**
 * Prepare user data for adding a new user
 */
export const prepareNewUserData = (email, permissions) => {
  return {
    email,
    username: email,
    ...permissions
  };
};

/**
 * Delete a specific field from a user's profile (admin action)
 */
export const deleteUserProfileField = async (email, field, resetTimer = false) => {
  try {
    const result = await AdminService.deleteUserProfileData(email, field, resetTimer);
    if (result.status === 200) {
      return {
        success: true,
        message: `User's ${field} deleted successfully!`,
        field: field
      };
    }
    return { success: false, error: `Failed to delete user's ${field}` };
  } catch (err) {
    return { success: false, error: `Failed to delete user's ${field}: ${err.message}` };
  }
};

/**
 * Reset the birthday restriction timer for a user, independent of birthday field
 */
export const resetUserBirthdayTimer = async (email) => {
  try {
    const result = await AdminService.resetBirthdayTimer(email);
    if (result.status === 200) {
      return {
        success: true,
        message: `Birthday restriction timer reset successfully!`
      };
    }
    return { success: false, error: 'Failed to reset birthday restriction timer' };
  } catch (err) {
    return { success: false, error: `Failed to reset birthday restriction timer: ${err.message}` };
  }
};
